import React, { useState, useEffect } from "react";
import { FaHeart, FaShoppingBag, FaUser } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import "./Preheader.css";

const Preheader = ({
  email,
  userName,
  cartCount,
  wishlistCount,
  products,
  onLogout,
}) => {
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  // ------------------------------------------------------
  // 🔥 Close dropdown when clicking outside
  // ------------------------------------------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".preheader-profile-dropdown")) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const input = e.target.value.toLowerCase().trim();
    setQuery(input);

    if (input.length > 0 && products.length > 0) {
      const matches = products
        .filter((p) => p.productName.toLowerCase().includes(input))
        .map((product) => ({
          name: product.productName,
          id: product.id,
          product,
        }));

      setFilteredSuggestions(matches);
      setShowSuggestions(true);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setShowSuggestions(false);

    if (filteredSuggestions.length > 0) {
      const firstMatch = filteredSuggestions[0];
      setShowDropdown(false);
      navigate(`/product/${firstMatch.id}`, {
        state: { product: firstMatch.product },
      });
    } else if (query.trim()) {
      setShowAlert(true);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.name);
    setShowSuggestions(false);
    setShowDropdown(false);
    navigate(`/product/${suggestion.id}`, {
      state: { product: suggestion.product },
    });
  };

  return (
    <>
      <div className="preheader">
        <div className="container">
          <div className="d-none d-md-flex align-items-center justify-content-between">

            {/* Logo */}
            <div className="preheader-logo">
              <Link to="/" className="text-decoration-none" onClick={() => setShowDropdown(false)}>
                <div className="preheader-mainlogotitle">
                  Kalanjiyam
                  <br />
                  <span className="preheader-subtitle">Boutique.</span>
                </div>
              </Link>
            </div>

            {/* Search */}
            <div className="preheader-search position-relative">
              <form onSubmit={handleSearch}>
                <input
                  type="text"
                  className="form-control preheader-search-input"
                  placeholder="I'm Shopping For..."
                  value={query}
                  onChange={handleInputChange}
                  onFocus={() => query && setShowSuggestions(true)}
                />
              </form>

              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="preheader-suggestions">
                  {filteredSuggestions.slice(0, 5).map((suggestion, i) => (
                    <div
                      key={i}
                      className="preheader-suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right buttons */}
            <div className="d-flex align-items-center gap-3">

              {/* Wishlist */}
              <Link
                to="/MyProfile/Wishlist"
                className="preheader-action-btn position-relative"
                onClick={() => setShowDropdown(false)}
              >
                <FaHeart className="me-2" />
                Wishlist
                {wishlistCount > 0 && (
                  <span className="count-badge">
                    {wishlistCount > 99 ? "99+" : wishlistCount}
                  </span>
                )}
              </Link>

              {/* Cart */}
              <Link
                to="/MyProfile/Addtocart"
                className="preheader-action-btn position-relative"
                onClick={() => setShowDropdown(false)}
              >
                <FaShoppingBag className="me-2" />
                Cart
                {cartCount > 0 && (
                  <span className="count-badge">
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
              </Link>

              {/* Profile / Login */}
              <div className="preheader-profile-dropdown position-relative">

                {email ? (
                  <>
                    <button
                      className="preheader-profile-btn"
                      onClick={() => setShowDropdown(!showDropdown)}
                    >
                      <div className="preheader-avatar">
                        {userName?.charAt(0).toUpperCase()}
                      </div>
                      <span className="ms-2">My Account</span>
                    </button>

                    {showDropdown && (
                      <div className="preheader-dropdown-menu">

                        <Link
                          to="/myProfile"
                          className="preheader-dropdown-item"
                          onClick={() => setShowDropdown(false)}
                        >
                          My Profile
                        </Link>

                        <Link
                          to="/TrackYourOrder/myOrders"
                          className="preheader-dropdown-item"
                          onClick={() => setShowDropdown(false)}
                        >
                          My Orders
                        </Link>

                        <button
                          onClick={() => {
                            setShowDropdown(false);
                            onLogout();
                          }}
                          className="preheader-dropdown-item preheader-dropdown-logout"
                        >
                          Log Out
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="preheader-login-btn"
                    onClick={() => setShowDropdown(false)}
                  >
                    <FaUser className="me-2" />
                    Log In
                  </Link>
                )}
              </div>
            </div>

          </div>

          <div className="d-flex d-md-none align-items-center justify-content-between flex-row no-wrap">
            {/* Logo */}
            <div className="preheader-logo">
              <Link to="/" className="text-decoration-none">
                <div className="preheader-mainlogotitle mobile">
                  Kalanjiyam
                  <span className="preheader-subtitle mobile">Boutique.</span>
                </div>
              </Link>
            </div>

            {/* Search Bar */}
            <div className="preheader-search mobile position-relative">
              <form onSubmit={handleSearch}>
                <input
                  type="text"
                  className="form-control preheader-search-input"
                  placeholder="Search..."
                  value={query}
                  onChange={handleInputChange}
                  onFocus={() => query.length > 0 && setShowSuggestions(true)}
                />
              </form>
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="preheader-suggestions">
                  {filteredSuggestions.slice(0, 5).map((suggestion, i) => (
                    <div
                      key={i}
                      className="preheader-suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Alert Modal */}
      {showAlert && (
        <div className="modal show preheader-alert-modal" style={{ display: "block" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">Dear User</h5>
                <button className="btn-close" onClick={() => setShowAlert(false)} />
              </div>
              <div className="modal-body">
                <p className="text-danger">No matching products found. Please try again.</p>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </div>
      )}
    </>
  );
};

export default Preheader;
